import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, Cloud, KeyRound, ScrollText, Clock, Loader2,
  PlayCircle, CheckCircle2, XCircle, ExternalLink, AlertTriangle, Trash2,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- API helpers ----
const fetchStatus = async () => {
  const { data } = await axios.get(`${API}/audit/data-safety`, { withCredentials: true });
  return data;
};
const fetchAuditEvents = async () => {
  const { data } = await axios.get(`${API}/audit/events`, { params: { limit: 100 }, withCredentials: true });
  return data;
};
const fetchAuditSummary = async () => {
  const { data } = await axios.get(`${API}/audit/summary`, { withCredentials: true });
  return data;
};
const fetchBackupHistory = async () => {
  const { data } = await axios.get(`${API}/audit/backup-history`, { params: { limit: 25 }, withCredentials: true });
  return data;
};
const triggerBackup = async () => {
  const { data } = await axios.post(`${API}/audit/backup-now`, {}, { withCredentials: true });
  return data;
};

// ---- Small display helpers ----
const StatusPill = ({ ok, labelOk = "Configured", labelBad = "Not configured" }) => (
  <span
    data-state={ok ? "ok" : "bad"}
    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm border ${
      ok
        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
        : "bg-red-50 border-red-200 text-red-800"
    }`}
  >
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
    {ok ? labelOk : labelBad}
  </span>
);

const humanBytes = (n) => {
  if (!n && n !== 0) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const relTime = (iso) => {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const DS_TABS = [
  { id: "overview", label: "Overview" },
  { id: "backup", label: "Backups" },
  { id: "audit", label: "Audit log" },
  { id: "reset", label: "Reset data" },
];

// ---- Page ----
const DataSafety = () => {
  const qc = useQueryClient();
  const { hasModule } = useAuth();
  const visibleDsTabs = useMemo(
    () => DS_TABS.filter((t) => hasModule(`data_safety.${t.id}`)),
    [hasModule],
  );
  const [tab, setTab] = useState(visibleDsTabs[0]?.id || "overview");
  useEffect(() => {
    if (visibleDsTabs.length && !visibleDsTabs.some((t) => t.id === tab)) {
      setTab(visibleDsTabs[0].id);
    }
  }, [visibleDsTabs, tab]);

  const statusQ = useQuery({ queryKey: ["data-safety"], queryFn: fetchStatus, refetchInterval: 30_000 });
  const eventsQ = useQuery({ queryKey: ["audit-events"], queryFn: fetchAuditEvents, enabled: tab === "audit" });
  const summaryQ = useQuery({ queryKey: ["audit-summary"], queryFn: fetchAuditSummary, enabled: tab === "audit" || tab === "overview" });
  const backupsQ = useQuery({ queryKey: ["backup-history"], queryFn: fetchBackupHistory, enabled: tab === "backup" });

  const backupMut = useMutation({
    mutationFn: triggerBackup,
    onSuccess: (data) => {
      if (data.status === "success") {
        toast.success(`Backup complete — ${data.filename} (${humanBytes(data.encrypted_bytes)})`);
      } else {
        toast.error(`Backup failed — ${data.error || "unknown"}`, { duration: 10000 });
      }
      qc.invalidateQueries({ queryKey: ["data-safety"] });
      qc.invalidateQueries({ queryKey: ["backup-history"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Backup request failed"),
  });

  if (statusQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading data safety status…
      </div>
    );
  }
  if (statusQ.isError) {
    return (
      <div className="text-red-700 bg-red-50 border border-red-200 rounded p-4">
        Failed to load. Ensure you are signed in as director.
      </div>
    );
  }

  const s = statusQ.data;
  const encOk = !!s.encryption?.key_configured;
  const bkOk = !!s.backup?.sa_json_configured && !!s.backup?.folder_id_configured;
  const lastBk = s.backup?.latest;
  const allGreen = encOk && bkOk;

  return (
    <div className="space-y-6" data-testid="data-safety-page">
      {/* Header */}
      <header className="border-b border-slate-200 pb-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {allGreen ? (
            <ShieldCheck className="h-8 w-8 text-emerald-600" />
          ) : (
            <ShieldAlert className="h-8 w-8 text-amber-600" />
          )}
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Director · Data Safety</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              {allGreen ? "All systems protected" : "Configuration incomplete"}
            </h1>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">Last backup</p>
          <p className="font-mono text-sm text-slate-900" data-testid="last-backup-rel">
            {lastBk ? relTime(lastBk.started_at) : "never"}
          </p>
          {lastBk?.status === "success" && (
            <p className="text-[11px] text-emerald-700">{humanBytes(lastBk.encrypted_bytes)} · {lastBk.filename}</p>
          )}
          {lastBk?.status === "failed" && (
            <p className="text-[11px] text-red-700">Failed · see Backup tab</p>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1">
        {visibleDsTabs.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ---------- Overview ---------- */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Encryption */}
          <section className="border border-slate-200 rounded-sm bg-white p-4" data-testid="card-encryption">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound className="h-5 w-5 text-slate-700" />
              <h2 className="font-heading text-base font-bold text-slate-900">Field encryption</h2>
              <StatusPill ok={encOk} labelOk="Active" labelBad="Not configured" />
            </div>
            <p className="text-xs text-slate-600">
              Sensitive HR fields (PAN, Aadhaar, bank a/c, IFSC, monthly basic salary) are encrypted at rest with AES-256-GCM.
            </p>
            <p className="text-[11px] text-slate-500 mt-2">
              Mode: <span className="font-mono">{s.encryption.mode}</span>
            </p>
          </section>

          {/* Backup config */}
          <section className="border border-slate-200 rounded-sm bg-white p-4" data-testid="card-backup-config">
            <div className="flex items-center gap-2 mb-3">
              <Cloud className="h-5 w-5 text-slate-700" />
              <h2 className="font-heading text-base font-bold text-slate-900">Cloud backup</h2>
              <StatusPill ok={bkOk} labelOk="Ready" labelBad="Missing config" />
            </div>
            <div className="text-xs text-slate-600 space-y-1">
              <p><span className="font-semibold">Destination:</span> Google Drive Shared Drive</p>
              <p><span className="font-semibold">Service account:</span> <span className="font-mono text-[10px]">{s.backup.sa_json_client_email || "-"}</span></p>
              <p><span className="font-semibold">Folder ID:</span> {s.backup.folder_id_configured ? "configured" : "missing"}</p>
              <p><span className="font-semibold">Schedule:</span> 02:00 IST daily · {s.backup?.retention_label || "kept forever"}</p>
            </div>
          </section>

          {/* Audit log */}
          <section className="border border-slate-200 rounded-sm bg-white p-4" data-testid="card-audit">
            <div className="flex items-center gap-2 mb-3">
              <ScrollText className="h-5 w-5 text-slate-700" />
              <h2 className="font-heading text-base font-bold text-slate-900">Audit log</h2>
              <StatusPill ok={true} labelOk="Recording" labelBad="Not recording" />
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900" data-testid="audit-total">
              {s.audit.total_events.toLocaleString()}<span className="text-sm text-slate-500 font-semibold ml-1">events</span>
            </p>
            <p className="text-[11px] text-slate-500 mt-1">Last event: {relTime(s.audit.last_event_at)}</p>
            {summaryQ.data?.top_actors_7d?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] tracking-widest uppercase font-semibold text-slate-500 mb-1.5">Top actors — last 7 days</p>
                <ul className="space-y-0.5 text-[11px]">
                  {summaryQ.data.top_actors_7d.slice(0, 5).map((a) => (
                    <li key={a.user} className="flex justify-between font-mono">
                      <span className="text-slate-700 truncate max-w-[220px]">{a.user}</span>
                      <span className="text-slate-500">{a.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Session */}
          <section className="border border-slate-200 rounded-sm bg-white p-4" data-testid="card-session">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-5 w-5 text-slate-700" />
              <h2 className="font-heading text-base font-bold text-slate-900">Session security</h2>
              <StatusPill ok={true} labelOk="Enforced" labelBad="Off" />
            </div>
            <p className="text-xs text-slate-600">Idle auto-logout: <span className="font-mono font-semibold">{s.session.idle_timeout_hours} hours</span></p>
            <p className="text-[11px] text-slate-500 mt-1">Users are warned 5 minutes before logout.</p>
            <p className="text-[11px] text-slate-400 mt-2 italic">2FA for Director role — coming soon (backlog).</p>
          </section>
        </div>
      )}

      {/* ---------- Backup tab ---------- */}
      {tab === "backup" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap bg-slate-50 border border-slate-200 rounded-sm px-4 py-3">
            <div>
              <p className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">Manual trigger</p>
              <p className="text-sm text-slate-800">Run an encrypted backup right now. Uploads to your Shared Drive in ~2 seconds.</p>
            </div>
            <Button
              data-testid="backup-now-btn"
              onClick={() => backupMut.mutate()}
              disabled={backupMut.isPending || !bkOk || !encOk}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {backupMut.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Backing up…</>
              ) : (
                <><PlayCircle className="h-4 w-4 mr-1.5" /> Backup now</>
              )}
            </Button>
          </div>

          {backupsQ.isLoading && <p className="text-slate-400 text-sm">Loading history…</p>}
          {backupsQ.data?.backups?.length === 0 && (
            <p className="text-slate-500 text-sm italic">No backups yet.</p>
          )}
          {backupsQ.data?.backups?.length > 0 && (
            <div className="border border-slate-200 rounded-sm bg-white overflow-hidden">
              <table className="w-full text-sm" data-testid="backup-history-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] tracking-widest uppercase font-semibold text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Started</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Filename</th>
                    <th className="text-right px-3 py-2">Size</th>
                    <th className="text-right px-3 py-2">Duration</th>
                    <th className="text-left px-3 py-2">By</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {backupsQ.data.backups.map((b) => (
                    <tr key={b.id} data-testid={`backup-row-${b.id}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-[11px]">{b.started_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-2">
                        <StatusPill ok={b.status === "success"} labelOk="OK" labelBad={b.status === "failed" ? "Failed" : b.status} />
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] truncate max-w-[220px]">{b.filename || "-"}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">{humanBytes(b.encrypted_bytes)}</td>
                      <td className="px-3 py-2 text-right text-[11px] text-slate-500">{b.duration_ms ? `${b.duration_ms} ms` : "-"}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-500">{b.triggered_by}</td>
                      <td className="px-3 py-2 text-right">
                        {b.drive_web_link && (
                          <a href={b.drive_web_link} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1 text-[11px]">
                            View <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ---------- Audit log tab ---------- */}
      {tab === "audit" && (
        <div className="space-y-4">
          {eventsQ.isLoading && <p className="text-slate-400 text-sm">Loading audit events…</p>}
          {eventsQ.data?.events?.length > 0 && (
            <div className="border border-slate-200 rounded-sm bg-white overflow-hidden">
              <table className="w-full text-sm" data-testid="audit-events-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] tracking-widest uppercase font-semibold text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">Entity</th>
                    <th className="text-right px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {eventsQ.data.events.map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-[11px]" title={e.at}>{relTime(e.at)}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-700">{e.user_email || "-"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-900">{e.action}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-600">{e.entity || "-"}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px]">
                        <span className={
                          e.extra?.status >= 500 ? "text-red-700 font-semibold" :
                          e.extra?.status >= 400 ? "text-amber-700" :
                          "text-slate-500"
                        }>
                          {e.extra?.status ?? "-"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-400">{e.ip || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ---------- Reset (danger zone) tab ---------- */}
      {tab === "reset" && (
        <div className="space-y-6">
          <SfgSkuBackfillCard />
          <EmployeeDedupeCard />
          <DangerZone />
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// SFG SKU-name backfill — Director-only one-shot admin action.
// Dry-run then Apply, exposing the top missing (grade, slit_width_mm,
// thickness_mm) combos so the Director can create those masters first.
// ---------------------------------------------------------------------------
const SfgSkuBackfillCard = () => {
  const [preview, setPreview] = useState(null);     // dry-run result
  const [applied, setApplied] = useState(null);     // apply result
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runDry = async () => {
    setLoading(true);
    setApplied(null);
    try {
      const { data } = await axios.get(`${API}/admin/sfg-sku-backfill/dry-run`, { withCredentials: true });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry run failed");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/admin/sfg-sku-backfill/apply`, {}, { withCredentials: true });
      setApplied(data);
      setPreview(null);
      toast.success(`Stamped sku_name on ${data.stamped} SFG(s). ${data.will_remain_blank} still unmatched.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const stats = preview || applied;

  return (
    <section className="border border-slate-200 rounded-sm bg-white p-5" data-testid="card-sfg-sku-backfill">
      <div className="flex items-center gap-2 mb-2">
        <ScrollText className="h-5 w-5 text-slate-700" />
        <h2 className="font-heading text-base font-bold text-slate-900">Rebuild SFG SKU names</h2>
      </div>
      <p className="text-xs text-slate-600 max-w-3xl">
        Sweeps every SFG whose <code>sku_name</code> is blank and stamps it using the matching active SFG SKU master
        (exact match on grade, slit width, thickness). Safe to run multiple times — only fills blanks, never overwrites.
        Run the <strong>dry run</strong> first to see what will change.
      </p>

      <div className="mt-4 flex items-center gap-2">
        <Button
          variant="outline"
          data-testid="sfg-sku-backfill-dry-run"
          onClick={runDry}
          disabled={loading || applying}
          className="h-9"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1.5" />}
          Dry run
        </Button>
        <Button
          data-testid="sfg-sku-backfill-apply"
          onClick={apply}
          disabled={applying || !preview || preview.will_stamp === 0}
          className="h-9 bg-slate-900 hover:bg-slate-800 text-white"
          title={!preview ? "Run dry-run first" : preview.will_stamp === 0 ? "Nothing to stamp" : ""}
        >
          {applying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
          Apply {preview?.will_stamp ? `(${preview.will_stamp})` : ""}
        </Button>
      </div>

      {stats && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
            <p className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">
              {applied ? "Stamped" : "Will stamp"}
            </p>
            <p className="font-mono text-2xl font-bold text-emerald-700" data-testid="sfg-sku-backfill-will-stamp">
              {applied ? applied.stamped : stats.will_stamp}
            </p>
          </div>
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
            <p className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">Still blank (no master)</p>
            <p className="font-mono text-2xl font-bold text-amber-700" data-testid="sfg-sku-backfill-will-remain">
              {stats.will_remain_blank}
            </p>
          </div>
        </div>
      )}

      {stats?.missing_combos?.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold mb-1">
            Missing SFG SKU masters (top {stats.missing_combos.length})
          </p>
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-1.5 text-left font-semibold">Grade</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Slit width (mm)</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Thickness (mm)</th>
                  <th className="px-3 py-1.5 text-right font-semibold">SFG count</th>
                </tr>
              </thead>
              <tbody>
                {stats.missing_combos.map((c, i) => (
                  <tr key={`${c.grade}-${c.slit_width_mm}-${c.thickness_mm}-${i}`}
                      data-testid={`sfg-sku-missing-row-${i}`}
                      className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-1.5 font-mono text-slate-900">{c.grade || "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-right text-slate-700">{c.slit_width_mm}</td>
                    <td className="px-3 py-1.5 font-mono text-right text-slate-700">{c.thickness_mm}</td>
                    <td className="px-3 py-1.5 font-mono text-right text-amber-700 font-semibold">{c.sfg_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Create these as <code>Masters &gt; SFG SKU</code>, then run <strong>Apply</strong> again to stamp them.
          </p>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Employee dedupe — merge duplicate HR Codes in the employee master.
// Director-only one-shot admin action (preview → apply, idempotent).
// Fixes "same employee shows twice on the Salary page" when a leftover
// master doc shares an HR code with the real one.
// ---------------------------------------------------------------------------
const EmployeeDedupeCard = () => {
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    setApplied(null);
    try {
      const { data } = await axios.get(`${API}/hr/admin/dedupe-employees/preview`, { withCredentials: true });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/hr/admin/dedupe-employees/apply`, {}, { withCredentials: true });
      setApplied(data);
      setPreview(null);
      toast.success(`Merged ${data.duplicates_merged} duplicate employee(s). Open Salary → the row will refresh.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const dupCount = preview?.duplicate_count ?? 0;

  return (
    <section className="border border-slate-200 rounded-sm bg-white p-5" data-testid="card-employee-dedupe">
      <div className="flex items-center gap-2 mb-2">
        <Users className="h-5 w-5 text-slate-700" />
        <h2 className="font-heading text-base font-bold text-slate-900">Merge duplicate employees (by HR Code)</h2>
      </div>
      <p className="text-xs text-slate-600 max-w-3xl">
        Finds employee masters that share the same <code>HR Code</code> (e.g. two "Kuldeep Yadav / 9" docs)
        and merges each duplicate into the record with the highest <code>Monthly Salary</code>. All attendance,
        salary slips, advances and leave requests are re-pointed to the canonical employee. Safe to run again — the
        second call sees zero groups. Preview first to see exactly what will be merged.
      </p>

      <div className="mt-4 flex items-center gap-2">
        <Button
          variant="outline"
          data-testid="employee-dedupe-preview"
          onClick={runPreview}
          disabled={loading || applying}
          className="h-9"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1.5" />}
          Preview
        </Button>
        <Button
          data-testid="employee-dedupe-apply"
          onClick={apply}
          disabled={applying || !preview || dupCount === 0}
          className="h-9 bg-slate-900 hover:bg-slate-800 text-white"
          title={!preview ? "Run preview first" : dupCount === 0 ? "Nothing to merge" : ""}
        >
          {applying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
          Merge {dupCount ? `(${dupCount})` : ""}
        </Button>
      </div>

      {applied && (
        <div className="mt-4 border border-emerald-200 bg-emerald-50 rounded-sm p-3 text-xs text-emerald-900 space-y-0.5" data-testid="employee-dedupe-applied">
          <p><b>{applied.duplicates_merged}</b> duplicate(s) merged into <b>{applied.groups_resolved}</b> canonical employee(s).</p>
          <p>Moved: {applied.attendance_records_moved} attendance · {applied.salary_advance_summaries_moved} advance · {applied.leave_requests_moved} leaves. Dropped {applied.salary_records_dropped} stale salary slips (will regenerate).</p>
        </div>
      )}

      {preview && dupCount === 0 && (
        <p className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="employee-dedupe-empty">
          No duplicates found — every HR Code maps to a single employee.
        </p>
      )}

      {preview && preview.groups?.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold mb-1">
            {preview.groups.length} code(s) with duplicates — {dupCount} record(s) will be merged away
          </p>
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-1.5 text-left font-semibold">HR Code</th>
                  <th className="px-3 py-1.5 text-left font-semibold">Keep (canonical)</th>
                  <th className="px-3 py-1.5 text-left font-semibold">Merge away</th>
                </tr>
              </thead>
              <tbody>
                {preview.groups.map((g, i) => (
                  <tr key={`${g.code}-${i}`}
                      data-testid={`employee-dedupe-group-${g.code}`}
                      className="border-t border-slate-100 align-top">
                    <td className="px-3 py-1.5 font-mono text-slate-900">{g.code}</td>
                    <td className="px-3 py-1.5 text-slate-900">
                      <div className="font-semibold">{g.canonical.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        ₹{Number(g.canonical.monthly_basic_salary || 0).toLocaleString("en-IN")} ·
                        {g.canonical.skill_grade ? ` ${g.canonical.skill_grade} ·` : ""}
                        {g.canonical.active ? " active" : " inactive"}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-slate-700">
                      {g.duplicates.map((d, j) => (
                        <div key={d.id} className="text-[11px] mb-0.5" data-testid={`employee-dedupe-dup-${g.code}-${j}`}>
                          <span className="font-mono text-slate-900">{d.name}</span>
                          <span className="text-[10px] text-slate-500 ml-2">
                            ₹{Number(d.monthly_basic_salary || 0).toLocaleString("en-IN")}
                            {d.skill_grade ? ` · ${d.skill_grade}` : ""}
                            {d.active ? "" : " · inactive"}
                          </span>
                        </div>
                      ))}
                      {(g.duplicate_refs || []).map((r) => (
                        <div key={`refs-${r.id}`} className="text-[10px] text-slate-500 font-mono">
                          moves: {r.attendance_records} attendance · {r.salary_advance_summaries} advance · {r.leave_requests} leaves
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};


// ---------------------------------------------------------------------------
// Danger Zone — wipe transactional data (director-only, backup first).
// ---------------------------------------------------------------------------
const DangerZone = () => {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState("");
  const [ackBackup, setAckBackup] = useState(false);
  const previewQ = useQuery({
    queryKey: ["dataSafety", "resetPreview"],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/data-safety/reset-preview`, { withCredentials: true });
      return data;
    },
  });

  const resetMut = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(
        `${API}/data-safety/reset-transactional`,
        { confirm: "RESET" },
        { withCredentials: true, timeout: 120000 },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(
        `Wiped ${data.total_documents_wiped.toLocaleString()} documents. Backup saved to Drive.`,
        { duration: 8000 },
      );
      setConfirmText("");
      setAckBackup(false);
      qc.invalidateQueries();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || err.message || "Reset failed");
      toast.error(msg, { duration: 10000 });
    },
  });

  if (previewQ.isLoading) {
    return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;
  }
  if (previewQ.isError) {
    return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
      {previewQ.error?.response?.data?.detail || "Failed to load reset preview. Director access required."}
    </div>;
  }

  const p = previewQ.data;
  const totalToWipe = p.to_wipe.reduce((sum, r) => sum + r.count, 0);
  const canFire = confirmText === "RESET" && ackBackup && !resetMut.isPending;

  return (
    <div className="space-y-5" data-testid="danger-zone">
      <div className="border-2 border-red-400 bg-red-50 rounded-sm p-4 flex gap-3">
        <AlertTriangle className="h-6 w-6 text-red-700 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-heading text-base font-bold text-red-900">Danger zone — irreversible</p>
          <p className="text-sm text-red-800">
            This wipes every day-to-day transaction record so your team can start on fresh live data. A full encrypted backup is uploaded to Google Drive before anything is deleted; if that backup fails, the wipe is aborted.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="border border-red-200 rounded-sm bg-white overflow-hidden" data-testid="wipe-list">
          <header className="px-4 py-2.5 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <span className="text-[11px] tracking-widest uppercase font-bold text-red-900">Will be wiped</span>
            <span className="font-mono text-xs font-bold text-red-900">{totalToWipe.toLocaleString()} docs</span>
          </header>
          <table className="w-full text-xs">
            <tbody>
              {p.to_wipe.map((r) => (
                <tr key={r.name} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-1.5 font-mono text-[11px] text-slate-700">{r.name}</td>
                  <td className="px-4 py-1.5 text-right font-mono text-[11px] text-slate-900">{r.count.toLocaleString()}</td>
                </tr>
              ))}
              <tr className="bg-amber-50 border-t border-amber-200">
                <td className="px-4 py-1.5 font-mono text-[11px] text-amber-900">counters (reset to 0)</td>
                <td className="px-4 py-1.5 text-right font-mono text-[11px] text-amber-900">{p.counters.documents}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="border border-emerald-200 rounded-sm bg-white overflow-hidden" data-testid="keep-list">
          <header className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between">
            <span className="text-[11px] tracking-widest uppercase font-bold text-emerald-900">Kept safe</span>
            <CheckCircle2 className="h-4 w-4 text-emerald-800" />
          </header>
          <table className="w-full text-xs">
            <tbody>
              {p.to_keep.map((r) => (
                <tr key={r.name} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-1.5 font-mono text-[11px] text-slate-700">{r.name}</td>
                  <td className="px-4 py-1.5 text-right font-mono text-[11px] text-slate-900">{r.count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <section className="border border-slate-300 rounded-sm bg-white p-4 space-y-3">
        <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
          <input
            data-testid="ack-backup-checkbox"
            type="checkbox"
            checked={ackBackup}
            onChange={(e) => setAckBackup(e.target.checked)}
            className="mt-1 h-4 w-4"
          />
          <span>I understand a fresh backup will be uploaded to Google Drive and this wipe cannot be undone from the UI.</span>
        </label>
        <div>
          <label className="text-[11px] tracking-widest uppercase font-semibold text-slate-600 block mb-1">
            Type <span className="font-mono font-bold text-red-700">RESET</span> to enable the button
          </label>
          <input
            data-testid="reset-confirm-input"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="RESET"
            className="w-full max-w-xs border border-slate-300 rounded-sm px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Button
            data-testid="reset-fire-btn"
            disabled={!canFire}
            onClick={() => {
              if (window.confirm("Final check: backup then wipe transactional data. Continue?")) {
                resetMut.mutate();
              }
            }}
            className="bg-red-700 hover:bg-red-800 text-white rounded-sm font-semibold"
          >
            {resetMut.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Backing up &amp; wiping…</>
            ) : (
              <><Trash2 className="h-4 w-4 mr-2" />Backup &amp; wipe transactional data</>
            )}
          </Button>
          <span className="text-[11px] text-slate-500">Takes ~5–15 seconds. Do not close the page.</span>
        </div>
      </section>

      {resetMut.data && (
        <section className="border border-emerald-300 bg-emerald-50 rounded-sm p-4 text-sm" data-testid="reset-result">
          <p className="font-heading font-bold text-emerald-900 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> Reset complete
          </p>
          <p className="text-emerald-900 mt-1">
            {resetMut.data.total_documents_wiped.toLocaleString()} documents wiped · {resetMut.data.counters_reset} counters reset.
          </p>
          {resetMut.data.pre_reset_backup?.drive_web_link && (
            <a
              href={resetMut.data.pre_reset_backup.drive_web_link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-emerald-800 underline mt-1"
            >
              Open pre-reset backup in Drive <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </section>
      )}
    </div>
  );
};

export default DataSafety;
